import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import SEOEntityPage from './pages/SEOEntityPage';
import SEOListPage from './pages/SEOListPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogAdminPage from './pages/BlogAdminPage';
import RouteTracker from './utils/RouteTracker';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import CustomersPage from './pages/CustomersPage';
import ProductsPage from './pages/ProductsPage';
import ShopBarcodeLabelsPage from './pages/shop_admin/ShopBarcodeLabelsPage';
import ShopProductsBulkEditPage from './pages/shop_admin/ShopProductsBulkEditPage';
import EnvHealthPage from './pages/EnvHealthPage';
import RssRedirect from './pages/RssRedirect';
import PublicOrderTrackingPage from './pages/PublicOrderTrackingPage';
import PriceListPage from './pages/PriceListPage';
import PurchasesPage from './pages/PurchasesPage';
import OrdersPage from './pages/OrdersPage';
import InventoryPage from './pages/InventoryPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import ExpensesPage from './pages/ExpensesPage';
import BulkUploadPage from './pages/BulkUploadPage';
import FinancialManagementPage from './pages/FinancialManagementPage';
import AuditLogPage from './pages/AuditLogPage';
import FinancialReportsPage from './pages/FinancialReportsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ReportsDownloadPage from './pages/ReportsDownloadPage';
import AccountsPage from './pages/AccountsPage';
import VATTrackingPage from './pages/VATTrackingPage';
import ProfitTrackingPage from './pages/ProfitTrackingPage';
import ShopOrdersPage from './pages/ShopOrdersPage';
import PromoCodesPage from './pages/PromoCodesPage';
import RiderDashboardPage from './pages/RiderDashboardPage';
import ShopProductsPage from './pages/ShopProductsPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ShopAuthProvider } from './context/ShopAuthContext';
import { ShopCartProvider } from './context/ShopCartContext';
import { ShopAdminAuthProvider } from './context/ShopAdminAuthContext';
import { PWAInstaller } from './components/PWAInstaller';
import DashboardLayout from './components/DashboardLayout';
import { ShopLayout } from './pages/shop/ShopLayout';
import ShopHome from './pages/shop/ShopHome';
import ShopProducts from './pages/shop/ShopProducts';
import ShopProductDetail from './pages/shop/ShopProductDetail';
import ShopCart from './pages/shop/ShopCart';
import ShopCheckout from './pages/shop/ShopCheckout';
import ShopLogin from './pages/shop/ShopLogin';
import ShopRegister from './pages/shop/ShopRegister';
import ShopAccount from './pages/shop/ShopAccount';
import ShopOrderDetail from './pages/shop/ShopOrderDetail';
import { ShopAdminLayout } from './pages/shop_admin/ShopAdminLayout';
import ShopAdminLoginPage from './pages/shop_admin/ShopAdminLogin';
import ShopAdminHome from './pages/shop_admin/ShopAdminHome';
import ShopAdminUsersPage from './pages/shop_admin/ShopUsersPage';
import ShopPosPage from './pages/shop_admin/ShopPosPage';
import LandingInboxPage from './pages/LandingInboxPage';
import ShopCustomersPage from './pages/shop_admin/ShopCustomersPage';
import DeliverySettingsPage from './pages/shop_admin/DeliverySettingsPage';
import NotificationSettingsPage from './pages/shop_admin/NotificationSettingsPage';
import { ShopPermGuard } from './pages/shop_admin/ShopPermGuard';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <ShopAuthProvider>
        <ShopAdminAuthProvider>
          <ShopCartProvider>
            <BrowserRouter>
              <RouteTracker />
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />

                {/* Public SEO landing pages */}
                <Route path="/industries" element={<SEOListPage kind="industries" />} />
                <Route path="/industries/:slug" element={<SEOEntityPage kind="industry" />} />
                <Route path="/products" element={<SEOListPage kind="products" />} />
                <Route path="/products/:slug" element={<SEOEntityPage kind="product" />} />
                <Route path="/services" element={<SEOListPage kind="services" />} />
                <Route path="/services/:slug" element={<SEOEntityPage kind="service" />} />
                <Route path="/locations" element={<SEOListPage kind="locations" />} />
                <Route path="/location/:slug" element={<SEOEntityPage kind="location" />} />
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/health/env" element={<EnvHealthPage />} />
                <Route path="/blog/rss.xml" element={<RssRedirect />} />
                <Route path="/track/:orderNumber" element={<PublicOrderTrackingPage />} />

                {/* Online Store (public + customer-authed) */}
                <Route path="/shop" element={<ShopLayout />}>
                  <Route index element={<ShopHome />} />
                  <Route path="products" element={<ShopProducts />} />
                  <Route path="product/:id" element={<ShopProductDetail />} />
                  <Route path="cart" element={<ShopCart />} />
                  <Route path="checkout" element={<ShopCheckout />} />
                  <Route path="login" element={<ShopLogin />} />
                  <Route path="register" element={<ShopRegister />} />
                  <Route path="account" element={<ShopAccount />} />
                  <Route path="orders/:id" element={<ShopOrderDetail />} />
                </Route>

                {/* Shop back-office (completely separate from /dashboard) */}
                <Route path="/shop-admin/login" element={<ShopAdminLoginPage />} />
                <Route path="/shop-admin" element={<ShopAdminLayout />}>
                  <Route index element={<ShopAdminHome />} />
                  <Route path="pos" element={<ShopPermGuard perm="process_pos_sale"><ShopPosPage /></ShopPermGuard>} />
                  <Route path="orders" element={<ShopPermGuard perm="view_shop_orders"><ShopOrdersPage /></ShopPermGuard>} />
                  <Route path="products" element={<ShopPermGuard perm="manage_shop_products"><ShopProductsPage /></ShopPermGuard>} />
                  <Route path="products/bulk-edit" element={<ShopPermGuard perm="manage_shop_products"><ShopProductsBulkEditPage /></ShopPermGuard>} />
                  <Route path="barcode-labels" element={<ShopPermGuard perm="manage_shop_products"><ShopBarcodeLabelsPage /></ShopPermGuard>} />
                  <Route path="promos" element={<ShopPermGuard perm="manage_shop_promos"><PromoCodesPage /></ShopPermGuard>} />
                  <Route path="rider" element={<ShopPermGuard perm="manage_deliveries"><RiderDashboardPage /></ShopPermGuard>} />
                  <Route path="customers" element={<ShopPermGuard perm="view_shop_analytics"><ShopCustomersPage /></ShopPermGuard>} />
                  <Route path="delivery-settings" element={<ShopPermGuard perm="manage_shop_orders"><DeliverySettingsPage /></ShopPermGuard>} />
                  <Route path="users" element={<ShopPermGuard perm="manage_shop_staff"><ShopAdminUsersPage /></ShopPermGuard>} />
                  <Route path="notifications" element={<ShopPermGuard perm="manage_shop_staff"><NotificationSettingsPage /></ShopPermGuard>} />
                </Route>

                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                  <Route path="customers" element={<CustomersPage />} />
                  <Route path="products" element={<ProductsPage />} />
                  <Route path="price-lists" element={<AdminRoute><PriceListPage /></AdminRoute>} />
                  <Route path="purchases" element={<PurchasesPage />} />
                  <Route path="orders" element={<OrdersPage />} />
                  <Route path="inventory" element={<InventoryPage />} />
                  <Route path="expenses" element={<ExpensesPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="bulk-upload" element={<BulkUploadPage />} />
                  <Route path="financial-management" element={<FinancialManagementPage />} />
                  <Route path="audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="reports-download" element={<ReportsDownloadPage />} />
                  <Route path="accounts" element={<AccountsPage />} />
                  <Route path="vat-tracking" element={<VATTrackingPage />} />
                  <Route path="profit-tracking" element={<ProfitTrackingPage />} />
                  <Route path="users" element={<AdminRoute><UsersPage /></AdminRoute>} />
                  <Route path="blog" element={<AdminRoute><BlogAdminPage /></AdminRoute>} />
                  <Route path="landing" element={<LandingInboxPage />} />
                </Route>
              </Routes>
              <Toaster position="top-right" richColors />
              <PWAInstaller />
            </BrowserRouter>
          </ShopCartProvider>
        </ShopAdminAuthProvider>
      </ShopAuthProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
